// Copyright ©️ 2026 eVolve MEP, LLC

import React from 'react';

import type { ShopConfiguration, WorkCell, WorkCellTaskType } from '@evolvemep/api-ts';
import { Flex, Text } from '@mantine/core';
import type { ColDef } from 'ag-grid-enterprise';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { CellRenderer } from 'components/Mantine/CellRenderer';
import { lockedColDef } from 'helpers/ag-grid/baseColumnDef';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { isNil } from 'helpers/isNotNil';

import { EditWorkCellMenu } from './EditWorkCellMenu';
import { UnassignTaskTypeButton } from './UnassignTaskTypeButton';

export const getWorkCellsColumnDef = ({
  shopConfiguration,
  isDraftView,
  splitViewIsOpen,
}: {
  shopConfiguration: ShopConfiguration | undefined;
  isDraftView: boolean;
  splitViewIsOpen: boolean;
}): ColDef<WorkCell>[] => [
  {
    ...lockedColDef(),
    width: 32,
    cellRenderer: 'agGroupCellRenderer',
    cellStyle: { paddingRight: 0 },
  },
  {
    headerName: splitViewIsOpen ? (isDraftView ? 'New Work Cell Setup' : 'Existing Work Cell Setup') : 'Work Cells',
    valueGetter: ({ data }) => `${data?.workCellName}${data?.workCellDescription}`,
    tooltipValueGetter: ({ data }) =>
      `${data?.workCellName}${data?.workCellDescription?.trim() ? `: ${data.workCellDescription}` : ''}`,
    flex: 1,
    resizable: false,
    sortable: false,
    cellRenderer: CellRenderer<WorkCell>(({ data }) => (
      <Flex align="center" gap="xs">
        <EvolveIcon icon="WorkCells" light />
        <Text>{data.workCellName}</Text>
        <Text c="dimmed">{data.workCellDescription}</Text>
      </Flex>
    )),
    rowDrag: !splitViewIsOpen || isDraftView,
  },
  {
    ...lockedColDef('right'),
    width: 50,
    cellRenderer: CellRenderer<WorkCell>(({ node, api }) =>
      isNil(shopConfiguration) ? null : (
        <EditWorkCellMenu
          shopConfiguration={shopConfiguration}
          workCell={node.data}
          workCellUpdated={(d) => node.updateData(d)}
          onWorkCellDeleted={() => refreshGrid(api)}
          canDelete={isDraftView}
        />
      ),
    ),
    hide: splitViewIsOpen && !isDraftView,
  },
];

export const getWorkCellTaskTypeColDef = ({ canEdit }: { canEdit: boolean }): ColDef<WorkCellTaskType>[] => [
  {
    field: 'taskTypeName',
    tooltipField: 'taskTypeName',
    flex: 1,
    resizable: false,
    cellRenderer: CellRenderer<WorkCellTaskType>(({ value }) => (
      <Flex align="center" gap="xs">
        <EvolveIcon icon="WorkCellTask" light />
        {value}
      </Flex>
    )),
    sortable: false,
  },
  {
    ...lockedColDef('right'),
    width: 50,
    cellRenderer: CellRenderer<WorkCellTaskType>(({ data, api }) => (
      <UnassignTaskTypeButton workCellTaskType={data} onWorkCellTaskTypeUnassigned={() => refreshGrid(api)} />
    )),
    hide: !canEdit,
  },
];
